// src/components/member/MemberLogin.jsx
import React, { useState, useEffect } from "react";
import "./SharedMember.css";
import "./MemberLogin.css";
import { setAuthDisplay } from "../../authDisplay";

const API_BASE =
    process.env.REACT_APP_API_BASE ??
    (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");

export default function MemberLogin({ onSuccess }) {
    const [loginUser, setLoginUser] = useState("");
    const [loginPass, setLoginPass] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [pendingLogin, setPendingLogin] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const [forgotIdentifier, setForgotIdentifier] = useState("");
    const [forgotAccount, setForgotAccount] = useState("");
    const [forgotTempPassword, setForgotTempPassword] = useState("");
    const [forgotNewPassword, setForgotNewPassword] = useState("");
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
    const [forgotMessage, setForgotMessage] = useState("");
    const [forgotComplete, setForgotComplete] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotExpiresAt, setForgotExpiresAt] = useState(null);
    const [forgotSecondsLeft, setForgotSecondsLeft] = useState(0);
    const [showForgotTemp, setShowForgotTemp] = useState(false);
    const [showForgotNew, setShowForgotNew] = useState(false);
    const [showForgotConfirm, setShowForgotConfirm] = useState(false);

    // 🔁 Load remembered username
    useEffect(() => {
        const rememberedUser = localStorage.getItem("remember_username");
        if (rememberedUser) {
            setLoginUser(rememberedUser);
            setRememberMe(true);
        }
    }, []);

    useEffect(() => {
        if (!forgotExpiresAt || forgotComplete) {
            setForgotSecondsLeft(0);
            return undefined;
        }

        const updateCountdown = () => {
            setForgotSecondsLeft(Math.max(0, Math.ceil((forgotExpiresAt - Date.now()) / 1000)));
        };

        updateCountdown();
        const timer = window.setInterval(updateCountdown, 1000);
        return () => window.clearInterval(timer);
    }, [forgotExpiresAt, forgotComplete]);

    useEffect(() => {
        if (!forgotMessage || forgotComplete) return undefined;

        const timer = window.setTimeout(() => {
            setForgotMessage("");
        }, 5000);

        return () => window.clearTimeout(timer);
    }, [forgotMessage, forgotComplete]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (loading) return;

        setLoginError("");
        setLoading(true);

        try {
            const res = await fetch(`${API_BASE}/api/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username: loginUser.trim(),
                    password: loginPass,
                }),
            });

            const ct = res.headers.get("content-type") || "";
            const data = ct.includes("application/json")
                ? await res.json()
                : { ok: false, error: `Unexpected response (${res.status})` };

            if (!res.ok || !data.ok) {
                setLoginError(data?.error || `Login failed (${res.status})`);
                return;
            }

            // ✅ Save token & role
            if (data.passwordChangeRequired) {
                setPendingLogin(data);
                setMustChangePassword(true);
                setLoginError("");
                setLoginPass("");
                return;
            }

            localStorage.removeItem("token");
            localStorage.setItem("role", data.role || "user");
            localStorage.setItem("idname", data.idname || "");
            setAuthDisplay(data);

            // 🔥 Reset OVS admin session on fresh login
            localStorage.removeItem("soal_admin_ovs_session");

            // 💾 Remember Me logic (username only)
            if (rememberMe) {
                localStorage.setItem("remember_username", loginUser.trim());
            } else {
                localStorage.removeItem("remember_username");
            }

            onSuccess?.(data);

            setLoginPass("");
        } catch (err) {
            console.error("Login error:", err);
            setLoginError("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (loading || !pendingLogin?.token) return;

        setLoginError("");

        if (newPassword.length < 8) {
            setLoginError("Password must be at least 8 characters.");
            return;
        }

        if (newPassword !== confirmPassword) {
            setLoginError("Passwords do not match.");
            return;
        }

        try {
            setLoading(true);
            const res = await fetch(`${API_BASE}/api/change-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${pendingLogin.token}`,
                },
                body: JSON.stringify({ newPassword }),
            });

            const data = await res.json();
            if (!res.ok || !data.ok) {
                setLoginError(data?.error || "Password update failed.");
                return;
            }

            localStorage.removeItem("token");
            localStorage.setItem("role", pendingLogin.role || "user");
            localStorage.setItem("idname", pendingLogin.idname || "");
            setAuthDisplay({ ...pendingLogin, passwordChangeRequired: false });
            localStorage.removeItem("soal_admin_ovs_session");

            if (rememberMe) {
                localStorage.setItem("remember_username", pendingLogin.idname || loginUser.trim());
            } else {
                localStorage.removeItem("remember_username");
            }

            onSuccess?.({ ...pendingLogin, passwordChangeRequired: false });
        } catch (err) {
            console.error("Change password error:", err);
            setLoginError("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const openForgotPassword = () => {
        setForgotOpen(true);
        setForgotIdentifier(loginUser.trim());
        setForgotAccount("");
        setForgotTempPassword("");
        setForgotNewPassword("");
        setForgotConfirmPassword("");
        setForgotMessage("");
        setForgotComplete(false);
        setForgotExpiresAt(null);
        setShowForgotTemp(false);
        setShowForgotNew(false);
        setShowForgotConfirm(false);
    };

    const closeForgotPassword = () => {
        setForgotOpen(false);
        setForgotIdentifier("");
        setForgotAccount("");
        setForgotTempPassword("");
        setForgotNewPassword("");
        setForgotConfirmPassword("");
        setForgotMessage("");
        setForgotComplete(false);
        setForgotExpiresAt(null);
        setShowForgotTemp(false);
        setShowForgotNew(false);
        setShowForgotConfirm(false);
        setLoginPass("");
    };

    const requestTemporaryPassword = async (e) => {
        e?.preventDefault();
        if (forgotLoading) return;

        setForgotMessage("");
        const identifier = (forgotIdentifier || forgotAccount).trim();
        if (!identifier) {
            setForgotMessage("Please enter your email or username.");
            return;
        }

        try {
            setForgotLoading(true);
            const res = await fetch(`${API_BASE}/api/forgot-password/request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ identifier }),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setForgotMessage(data?.error || "Unable to send temporary password. Please contact the administrator.");
                return;
            }

            setForgotAccount(data.idname || identifier);
            setForgotIdentifier(data.idname || identifier);
            setForgotTempPassword("");
            setForgotNewPassword("");
            setForgotConfirmPassword("");
            setForgotComplete(false);
            setForgotExpiresAt(Date.now() + 10 * 60 * 1000);
            setForgotSecondsLeft(10 * 60);
            setForgotMessage(data.message || "Temporary password sent. Please check your email.");
        } catch (err) {
            console.error("Forgot password request error:", err);
            setForgotMessage("Network error. Please contact the administrator.");
        } finally {
            setForgotLoading(false);
        }
    };

    const formatForgotCountdown = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
    };

    const completeForgotPassword = async (e) => {
        e.preventDefault();
        if (forgotLoading || forgotComplete) return;

        setForgotMessage("");

        if (!forgotTempPassword.trim()) {
            setForgotMessage("Please enter the temporary password from your email.");
            return;
        }

        if (forgotNewPassword.length < 8) {
            setForgotMessage("New password must be at least 8 characters.");
            return;
        }

        if (forgotNewPassword !== forgotConfirmPassword) {
            setForgotMessage("New passwords do not match.");
            return;
        }

        try {
            setForgotLoading(true);
            const res = await fetch(`${API_BASE}/api/forgot-password/complete`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    idname: forgotAccount,
                    temporaryPassword: forgotTempPassword,
                    newPassword: forgotNewPassword,
                }),
            });
            const data = await res.json();

            if (!res.ok || !data.ok) {
                setForgotMessage(data?.error || "Password reset failed.");
                return;
            }

            setForgotComplete(true);
            setForgotMessage(data.message || "New password is complete. Please log in.");
            setLoginUser(forgotAccount || forgotIdentifier.trim());
        } catch (err) {
            console.error("Forgot password complete error:", err);
            setForgotMessage("Network error. Please try again.");
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <section
            className="login-wrap"
            style={{
                "--login-bg": `url("${process.env.PUBLIC_URL || ""}/larawan/TGPWP3D.png")`,
                "--login-mobile-bg": `url("${process.env.PUBLIC_URL || ""}/larawan/TGPWP3Mobile.png")`,
            }}
        >
            <div className="login-card">
                <h2 className="login-title">
                    {mustChangePassword ? "Create New Password" : "Member Sign In"}
                </h2>
                <p className="login-subtitle">
                    {mustChangePassword
                        ? "Please change your default password before continuing."
                        : "Please sign in to view the member profile page"}
                </p>

                {mustChangePassword ? (
                    <form onSubmit={handleChangePassword} className="login-form" autoComplete="off">
                        <div className="form-row">
                            <label htmlFor="newPassword">New Password</label>
                            <div className="password-wrapper reset-password-wrapper">
                                <input
                                    id="newPassword"
                                    type={showNewPassword ? "text" : "password"}
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="reset-toggle-password"
                                    onClick={() => setShowNewPassword((s) => !s)}
                                    disabled={loading}
                                    aria-label={showNewPassword ? "Hide new password" : "Unhide new password"}
                                    title={showNewPassword ? "Hide new password" : "Unhide new password"}
                                >
                                    <span>{showNewPassword ? "\u{1F441}\uFE0F" : "\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F"}</span>
                                </button>
                            </div>
                        </div>

                        <div className="form-row">
                            <label htmlFor="confirmPassword">Confirm Password</label>
                            <div className="password-wrapper reset-password-wrapper">
                                <input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="reset-toggle-password"
                                    onClick={() => setShowConfirmPassword((s) => !s)}
                                    disabled={loading}
                                    aria-label={showConfirmPassword ? "Hide confirm password" : "Unhide confirm password"}
                                    title={showConfirmPassword ? "Hide confirm password" : "Unhide confirm password"}
                                >
                                    <span>{showConfirmPassword ? "\u{1F441}\uFE0F" : "\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F"}</span>
                                </button>
                            </div>
                        </div>

                        {loginError && <div className="login-error">{loginError}</div>}

                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? "Saving..." : "Save Password"}
                        </button>
                    </form>
                ) : (
                <form onSubmit={handleLogin} className="login-form" autoComplete="off">
                    <div className="form-row">
                        <label htmlFor="username">Email or Username</label>
                        <input
                            id="username"
                            type="text"
                            placeholder="Enter email or username"
                            value={loginUser}
                            onChange={(e) => setLoginUser(e.target.value)}
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-row">
                        <label htmlFor="password">Password</label>
                        <div className="password-wrapper">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter password"
                                value={loginPass}
                                onChange={(e) => setLoginPass(e.target.value)}
                                required
                                disabled={loading}
                                style={{ paddingRight: 38 }}
                            />
                            <button
                                type="button"
                                className="toggle-password"
                                onClick={() => setShowPassword((s) => !s)}
                                disabled={loading}
                                title={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? "👁️" : "👁️‍🗨️"}
                            </button>
                        </div>
                    </div>

                    {/* 🔴 Error message */}
                    {loginError && (
                        <div className="login-error">{loginError}</div>
                    )}

                    {/* ✅ Remember Me */}
                    <div className="remember-row">
                        <label className="remember-label">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                disabled={loading}
                            />
                            <span>Remember me</span>
                        </label>
                        <button
                            type="button"
                            className="forgot-password-link"
                            onClick={openForgotPassword}
                            disabled={loading}
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <button type="submit" className="btn-primary" disabled={loading}>
                        {loading ? "Signing in..." : "Sign In"}
                    </button>

                    <div className="login-note">
                        <strong>Hint:</strong> Login using the username &amp; password provided by the admin.
                    </div>
                </form>
                )}
            </div>

            {forgotOpen && (
                <div className="forgot-modal-overlay" onClick={closeForgotPassword}>
                    <div className="forgot-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="forgot-modal-close"
                            onClick={closeForgotPassword}
                            aria-label="Close forgot password"
                        >
                            X
                        </button>

                        <h3>Forgot Password</h3>

                        {!forgotAccount ? (
                            <form className="login-form" onSubmit={requestTemporaryPassword}>
                                <div className="form-row">
                                    <label htmlFor="forgotIdentifier">Email or Username</label>
                                    <input
                                        id="forgotIdentifier"
                                        type="text"
                                        placeholder="Enter email or username"
                                        value={forgotIdentifier}
                                        onChange={(e) => setForgotIdentifier(e.target.value)}
                                        disabled={forgotLoading}
                                        required
                                    />
                                </div>

                                {forgotMessage && <div className="login-error">{forgotMessage}</div>}

                                <button type="submit" className="btn-primary" disabled={forgotLoading}>
                                    {forgotLoading ? "Sending..." : "Send Temporary Password"}
                                </button>
                            </form>
                        ) : (
                            <form className="login-form" onSubmit={completeForgotPassword}>
                                <p className="forgot-instruction">
                                    Enter the temporary password from your email, then create your new password.
                                </p>

                                <div className="form-row">
                                    <label htmlFor="tempPassword">Temporary Password</label>
                                    <div className="password-wrapper reset-password-wrapper">
                                        <input
                                            id="tempPassword"
                                            type={showForgotTemp ? "text" : "password"}
                                            placeholder="Enter temporary password"
                                            value={forgotTempPassword}
                                            onChange={(e) => setForgotTempPassword(e.target.value)}
                                            disabled={forgotLoading || forgotComplete}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="reset-toggle-password"
                                            onClick={() => setShowForgotTemp((s) => !s)}
                                            disabled={forgotLoading || forgotComplete}
                                            aria-label={showForgotTemp ? "Hide temporary password" : "Unhide temporary password"}
                                            title={showForgotTemp ? "Hide temporary password" : "Unhide temporary password"}
                                        >
                                            <span>{showForgotTemp ? "\u{1F441}\uFE0F" : "\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F"}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="forgotNewPassword">Create New Password</label>
                                    <div className="password-wrapper reset-password-wrapper">
                                        <input
                                            id="forgotNewPassword"
                                            type={showForgotNew ? "text" : "password"}
                                            placeholder="Create new password"
                                            value={forgotNewPassword}
                                            onChange={(e) => setForgotNewPassword(e.target.value)}
                                            disabled={forgotLoading || forgotComplete}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="reset-toggle-password"
                                            onClick={() => setShowForgotNew((s) => !s)}
                                            disabled={forgotLoading || forgotComplete}
                                            aria-label={showForgotNew ? "Hide new password" : "Unhide new password"}
                                            title={showForgotNew ? "Hide new password" : "Unhide new password"}
                                        >
                                            <span>{showForgotNew ? "\u{1F441}\uFE0F" : "\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F"}</span>
                                        </button>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <label htmlFor="forgotConfirmPassword">Confirm New Password</label>
                                    <div className="password-wrapper reset-password-wrapper">
                                        <input
                                            id="forgotConfirmPassword"
                                            type={showForgotConfirm ? "text" : "password"}
                                            placeholder="Confirm new password"
                                            value={forgotConfirmPassword}
                                            onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                            disabled={forgotLoading || forgotComplete}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="reset-toggle-password"
                                            onClick={() => setShowForgotConfirm((s) => !s)}
                                            disabled={forgotLoading || forgotComplete}
                                            aria-label={showForgotConfirm ? "Hide confirm password" : "Unhide confirm password"}
                                            title={showForgotConfirm ? "Hide confirm password" : "Unhide confirm password"}
                                        >
                                            <span>{showForgotConfirm ? "\u{1F441}\uFE0F" : "\u{1F441}\uFE0F\u200D\u{1F5E8}\uFE0F"}</span>
                                        </button>
                                    </div>
                                </div>

                                {!forgotComplete && (
                                    <div className="forgot-countdown">
                                        {forgotSecondsLeft > 0 ? (
                                            <>Temporary password expires in {formatForgotCountdown(forgotSecondsLeft)}</>
                                        ) : (
                                            <button
                                                type="button"
                                                className="forgot-resend-button"
                                                onClick={requestTemporaryPassword}
                                                disabled={forgotLoading}
                                            >
                                                {forgotLoading ? "Sending..." : "Send Temporary Password Again"}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {forgotMessage && (
                                    <div className={forgotComplete ? "login-success" : "login-error"}>
                                        {forgotMessage}
                                    </div>
                                )}

                                {forgotComplete ? (
                                    <button type="button" className="btn-primary" onClick={closeForgotPassword}>
                                        Close and Log In
                                    </button>
                                ) : forgotSecondsLeft > 0 ? (
                                    <button type="submit" className="btn-primary" disabled={forgotLoading}>
                                        {forgotLoading ? "Saving..." : "Save New Password"}
                                    </button>
                                ) : (
                                    <button type="button" className="btn-primary" disabled>
                                        Temporary Password Expired
                                    </button>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
